import { createTheme } from '@mui/material/styles';

// Golf-themed color palette inspired by Alexa Travel Agency
const colors = {
  // Primary - Golf Green
  golfGreen: {
    main: '#2e7d32',
    light: '#4caf50',
    dark: '#1b5e20',
    contrastText: '#ffffff',
  },
  // Secondary - Sand/Beige
  sand: {
    main: '#d4a574',
    light: '#f5f5dc',
    dark: '#a67c52',
    contrastText: '#000000',
  },
  // Accent - Sky Blue
  skyBlue: {
    main: '#0288d1',
    light: '#03a9f4',
    dark: '#01579b',
    contrastText: '#ffffff',
  },
  // Status colors
  status: {
    pending: '#9e9e9e',
    visited: '#2196f3',
    processing: '#9c27b0',
    success: '#4caf50',
    failed: '#f44336',
    expired: '#ff9800',
  },
};

// Create custom golf-themed MUI theme
const golfTheme = createTheme({
  palette: {
    primary: {
      main: colors.golfGreen.main,
      light: colors.golfGreen.light,
      dark: colors.golfGreen.dark,
      contrastText: colors.golfGreen.contrastText,
    },
    secondary: {
      main: colors.sand.main,
      light: colors.sand.light,
      dark: colors.sand.dark,
      contrastText: colors.sand.contrastText,
    },
    info: {
      main: colors.skyBlue.main,
      light: colors.skyBlue.light,
      dark: colors.skyBlue.dark,
      contrastText: colors.skyBlue.contrastText,
    },
    success: {
      main: colors.status.success,
    },
    error: {
      main: colors.status.failed,
    },
    warning: {
      main: colors.status.expired,
    },
    background: {
      default: '#f5f5f5',
      paper: '#ffffff',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontFamily: '"Playfair Display", serif',
      fontWeight: 700,
      fontSize: '2.5rem',
      '@media (max-width:600px)': {
        fontSize: '2rem',
      },
    },
    h2: {
      fontFamily: '"Playfair Display", serif',
      fontWeight: 700,
      fontSize: '2rem',
      '@media (max-width:600px)': {
        fontSize: '1.75rem',
      },
    },
    h3: {
      fontFamily: '"Playfair Display", serif',
      fontWeight: 700,
      fontSize: '1.75rem',
      '@media (max-width:600px)': {
        fontSize: '1.5rem',
      },
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.5rem',
      '@media (max-width:600px)': {
        fontSize: '1.25rem',
      },
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.25rem',
      '@media (max-width:600px)': {
        fontSize: '1.1rem',
      },
    },
    h6: {
      fontWeight: 600,
      fontSize: '1rem',
      '@media (max-width:600px)': {
        fontSize: '0.95rem',
      },
    },
    button: {
      textTransform: 'none',
      fontWeight: 600,
    },
  },
  shape: {
    borderRadius: 12,
  },
  shadows: [
    'none',
    '0px 2px 4px rgba(0,0,0,0.05)',
    '0px 4px 8px rgba(0,0,0,0.08)',
    '0px 6px 12px rgba(0,0,0,0.1)',
    '0px 8px 16px rgba(0,0,0,0.12)',
    '0px 10px 20px rgba(0,0,0,0.14)',
    '0px 12px 24px rgba(0,0,0,0.16)',
    '0px 14px 28px rgba(0,0,0,0.18)',
    '0px 16px 32px rgba(0,0,0,0.2)',
    '0px 18px 36px rgba(0,0,0,0.22)',
    '0px 20px 40px rgba(0,0,0,0.24)',
    '0px 22px 44px rgba(0,0,0,0.26)',
    '0px 24px 48px rgba(0,0,0,0.28)',
    '0px 26px 52px rgba(0,0,0,0.3)',
    '0px 28px 56px rgba(0,0,0,0.32)',
    '0px 30px 60px rgba(0,0,0,0.34)',
    '0px 32px 64px rgba(0,0,0,0.36)',
    '0px 34px 68px rgba(0,0,0,0.38)',
    '0px 36px 72px rgba(0,0,0,0.4)',
    '0px 38px 76px rgba(0,0,0,0.42)',
    '0px 40px 80px rgba(0,0,0,0.44)',
    '0px 42px 84px rgba(0,0,0,0.46)',
    '0px 44px 88px rgba(0,0,0,0.48)',
    '0px 46px 92px rgba(0,0,0,0.5)',
    '0px 48px 96px rgba(0,0,0,0.52)',
  ],
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '10px 24px',
          fontSize: '1rem',
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0px 4px 8px rgba(0,0,0,0.15)',
          },
          // Touch-friendly minimum size for mobile
          minHeight: 44,
          '@media (max-width:600px)': {
            minHeight: 48,
            fontSize: '1rem',
          },
        },
        sizeLarge: {
          padding: '14px 32px',
          fontSize: '1.1rem',
          minHeight: 48,
          '@media (max-width:600px)': {
            minHeight: 52,
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0px 4px 12px rgba(0,0,0,0.08)',
          transition: 'box-shadow 0.3s ease',
          '&:hover': {
            boxShadow: '0px 8px 24px rgba(0,0,0,0.12)',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            // Touch-friendly input height for mobile
            '@media (max-width:600px)': {
              '& input': {
                padding: '16px 14px',
              },
            },
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 600,
          fontSize: '0.875rem',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 16,
          // Full screen on small mobile devices
          '@media (max-width:600px)': {
            margin: 16,
            maxHeight: 'calc(100% - 32px)',
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          // Better touch targets for mobile tables
          '@media (max-width:600px)': {
            padding: '12px 8px',
            fontSize: '0.875rem',
          },
        },
        head: {
          fontWeight: 700,
          backgroundColor: colors.golfGreen.light,
          color: '#ffffff',
        },
      },
    },
  },
});

// Export status colors for use in components
export { colors };
export default golfTheme;
